/**
 * AdminUsersPage — admin-only user list + one-click password reset.
 *
 * Routed at `/admin/users`. Visible in the topbar nav only when the
 * authenticated user has role === "admin". Pulls from:
 *   GET  /api/admin/users
 *   POST /api/admin/users/{email}/reset-password   (body: { new_password })
 *
 * Removes the need to set `ADMIN_PASSWORD` env vars + redeploy for routine
 * password support, and works regardless of Resend DNS state.
 */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Topbar from "@/components/Topbar";
import {
  ShieldCheck, MagnifyingGlass, Key, X, Copy, ArrowsClockwise, CurrencyDollar,
  EnvelopeSimple, CheckCircle, WarningCircle, XCircle, Trash, Prohibit,
} from "@phosphor-icons/react";

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const ok = s === "verified" || s === "success" || s === "ok";
  const pending = s === "pending" || s === "not_started" || s === "in_progress";
  if (ok) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
        <CheckCircle size={10} weight="fill" /> {status || "verified"}
      </span>
    );
  }
  if (pending) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-[#FFCC00]/20 text-[#FFCC00] border border-[#FFCC00]/40">
        <WarningCircle size={10} weight="fill" /> {status || "pending"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-500/20 text-red-300 border border-red-500/40">
      <XCircle size={10} weight="fill" /> {status || "failed"}
    </span>
  );
}

function EmailDiagnosticCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);
  const [testTo, setTestTo]     = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testOut, setTestOut]   = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/admin/email/diagnostics");
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Diagnostics failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!data) {
    return (
      <div className="border border-white/10 bg-[#0a0d12] p-4 mb-6 font-mono text-xs text-gray-500"
           data-testid="email-diagnostic-loading">
        Probing Resend integration…
      </div>
    );
  }

  const records = data?.matching_domain?.records || [];
  const overallVerified =
    (data?.matching_domain?.status || "").toLowerCase() === "verified";

  return (
    <div className="border border-white/10 bg-[#0a0d12] mb-6 font-mono"
         data-testid="email-diagnostic-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors text-left"
        data-testid="email-diagnostic-toggle">
        <div className="flex items-center gap-3">
          <EnvelopeSimple size={18} className="text-[#FFCC00]" />
          <div>
            <div className="label-tag">// Resend email integration</div>
            <div className="text-sm text-white">
              {data.sender_email || "no sender configured"} →{" "}
              {data.sender_domain ? (
                overallVerified
                  ? <span className="text-emerald-400">domain verified ✓</span>
                  : <span className="text-[#FFCC00]">needs attention</span>
              ) : <span className="text-red-400">not configured</span>}
            </div>
          </div>
        </div>
        <div className="text-[10px] text-gray-500">{open ? "▼" : "▶"}</div>
      </button>

      {open && (
        <div className="border-t border-white/10 p-4 space-y-3 text-[11px]">
          {/* Top-level errors */}
          {data.error && (
            <div className="border-l-2 border-[#FFCC00] bg-[#FFCC00]/10 pl-3 py-2 text-[#FFE16B]"
                 data-testid="email-diagnostic-error">
              {data.error}
            </div>
          )}
          {data.using_restricted_key && !data.error && (
            <div className="border-l-2 border-gray-500 bg-white/5 pl-3 py-2 text-gray-400">
              Using a send-only Resend key for delivery — domain status hidden.
            </div>
          )}

          {/* Config summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-gray-400">
            <div>
              <span className="text-gray-500">RESEND_API_KEY:</span>{" "}
              <span className={data.resend_configured ? "text-emerald-400" : "text-red-400"}>
                {data.resend_configured ? "configured" : "missing"}
              </span>
              {data.api_key_meta?.preview && (
                <div className="mt-1 pl-4 text-[10px] text-gray-500">
                  <span className="text-gray-400">preview:</span>{" "}
                  <span className="text-[#FFCC00]">{data.api_key_meta.preview}</span>
                  <span className="text-gray-600"> · {data.api_key_meta.length} chars</span>
                  {!data.api_key_meta.prefix_ok && (
                    <span className="ml-2 text-red-400" title="Resend keys must start with re_">
                      ⚠ missing `re_` prefix
                    </span>
                  )}
                  {data.api_key_meta.has_whitespace && (
                    <span className="ml-2 text-red-400" title="Trim leading/trailing whitespace">
                      ⚠ contains whitespace
                    </span>
                  )}
                </div>
              )}
            </div>
            <div>
              <span className="text-gray-500">FRONTEND_URL:</span>{" "}
              <span className="text-[#00E5FF] break-all">{data.frontend_url || "—"}</span>
            </div>
            <div>
              <span className="text-gray-500">SENDER_EMAIL:</span>{" "}
              <span className="text-[#00E5FF]">{data.sender_email || "—"}</span>
            </div>
            <div>
              <span className="text-gray-500">Sender domain:</span>{" "}
              <span className="text-[#00E5FF]">{data.sender_domain || "—"}</span>
            </div>
          </div>

          {/* Domain records (only when we could read them) */}
          {data.matching_domain ? (
            <div>
              <div className="flex items-center gap-2 mb-2 mt-3">
                <span className="text-gray-400">Domain:</span>
                <span className="text-[#00E5FF]">{data.matching_domain.name}</span>
                <StatusPill status={data.matching_domain.status} />
                {data.matching_domain.region && (
                  <span className="text-gray-500 text-[10px]">
                    · region {data.matching_domain.region}
                  </span>
                )}
              </div>
              {records.length > 0 ? (
                <div className="border border-white/10 overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-black/30">
                      <tr className="text-[9px] uppercase tracking-widest text-gray-500 text-left">
                        <th className="p-2">Type</th><th className="p-2">Host</th>
                        <th className="p-2">Value (truncated)</th><th className="p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((r, i) => {
                        const val = String(r.value || "");
                        const short = val.length > 60 ? `${val.slice(0, 60)}…` : val;
                        return (
                          <tr key={i} className="border-t border-white/5 text-[11px]">
                            <td className="p-2 text-[#FFCC00]">{r.record || r.type}</td>
                            <td className="p-2 text-gray-300">{r.name || "—"}</td>
                            <td className="p-2 text-gray-400 break-all">
                              {short}
                              {val.length > 60 && (
                                <button onClick={() => {
                                  navigator.clipboard.writeText(val);
                                  toast.success("Value copied");
                                }}
                                className="ml-2 text-[10px] text-[#00E5FF] hover:underline">
                                  copy
                                </button>
                              )}
                            </td>
                            <td className="p-2"><StatusPill status={r.status} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-gray-500">No DNS records returned — open the domain in Resend dashboard.</div>
              )}
            </div>
          ) : !data.error ? (
            <div className="text-gray-500 text-[11px]">
              No Resend domain matches{" "}
              <span className="text-[#FFCC00]">{data.sender_domain || "(none)"}</span>.
              Add the domain in Resend dashboard → Domains.
            </div>
          ) : null}

          {/* Action row */}
          <div className="flex items-center gap-2 pt-2">
            <button onClick={load} disabled={busy}
                    className="btn-ghost text-xs py-1 inline-flex items-center gap-1 disabled:opacity-40"
                    data-testid="email-diagnostic-refresh">
              <ArrowsClockwise size={12} /> {busy ? "Checking…" : "Re-check"}
            </button>
            <a href="https://resend.com/domains"
               target="_blank" rel="noreferrer"
               className="btn-ghost text-xs py-1 inline-flex items-center gap-1"
               data-testid="email-diagnostic-open-resend">
              Open Resend dashboard ↗
            </a>
          </div>

          {/* v3.2.51 · Live send-test — fires a synthetic email and
                surfaces Resend's raw response (or the exception detail).
                Lets admins diagnose delivery in prod without registering. */}
          <div className="pt-4 border-t border-white/5 mt-4">
            <div className="label-tag mb-2">// live send-test</div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="email"
                placeholder="admin@your-inbox.com"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                className="flex-1 min-w-[240px] bg-black/40 border border-white/10 text-white text-xs font-mono px-3 py-1.5 focus:border-[#00E5FF] outline-none"
                data-testid="email-diagnostic-test-to"
              />
              <button
                onClick={async () => {
                  if (!testTo) { toast.error("Enter an inbox to test"); return; }
                  setTestBusy(true); setTestOut(null);
                  try {
                    const { data: r } = await api.post("/admin/email/send-test", { to: testTo });
                    setTestOut(r);
                    if (r.ok) toast.success("Test email dispatched — check inbox and spam.");
                    else       toast.error(r.error || "Send failed");
                  } catch (err) {
                    setTestOut({ ok: false, error: err?.response?.data?.detail || err.message });
                    toast.error("Send failed");
                  } finally { setTestBusy(false); }
                }}
                disabled={testBusy}
                className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="email-diagnostic-test-send"
              >
                <EnvelopeSimple size={12} weight="fill" /> {testBusy ? "Sending…" : "Send test"}
              </button>
            </div>
            {testOut && (
              <pre className={`mt-3 text-[11px] font-mono p-3 border overflow-x-auto whitespace-pre-wrap break-all
                                ${testOut.ok
                                    ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-200"
                                    : "border-red-400/30 bg-red-400/5 text-red-200"}`}
                    data-testid="email-diagnostic-test-out">
                {JSON.stringify(testOut, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const [pw, setPw]   = useState("");
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pw || pw.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/admin/users/${encodeURIComponent(user.email)}/reset-password`,
        { new_password: pw },
      );
      toast.success(`Password updated for ${user.email}`);
      onDone && onDone();
      onClose && onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const generate = () => {
    // Friendly + strong: 14 chars, mixed alphabet (no ambiguous chars).
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*";
    let out = "";
    const arr = new Uint32Array(14);
    crypto.getRandomValues(arr);
    for (const n of arr) out += alphabet[n % alphabet.length];
    setPw(out);
    setShown(true);
  };

  return (
    <div className="fixed inset-0 z-[1500] bg-black/70 flex items-center justify-center p-4"
         onClick={onClose} data-testid="admin-reset-modal">
      <form className="bg-[#0a0d12] border border-[#FFCC00]/40 w-full max-w-md p-5 font-mono space-y-3"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}>
        <div className="flex items-center justify-between">
          <div>
            <div className="label-tag">// Reset password</div>
            <div className="text-base text-white mt-1 truncate">{user.email}</div>
            <div className="text-[10px] text-gray-500">{user.name || "—"} · {user.role}</div>
          </div>
          <button type="button" onClick={onClose}
                  className="btn-ghost p-1"
                  data-testid="admin-reset-close">
            <X size={14} />
          </button>
        </div>

        <div>
          <div className="label-tag text-[10px] mb-1">New password (min 8 chars)</div>
          <div className="flex gap-2">
            <input
              type={shown ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
              minLength={8}
              className="input-base text-sm flex-1"
              data-testid="admin-reset-pw"
            />
            <button type="button" onClick={() => setShown((s) => !s)}
                    className="btn-ghost text-xs px-2"
                    data-testid="admin-reset-toggle-visibility">
              {shown ? "Hide" : "Show"}
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button type="button" onClick={generate}
                    className="btn-ghost text-xs py-1 inline-flex items-center gap-1"
                    data-testid="admin-reset-generate">
              <ArrowsClockwise size={12} /> Generate strong
            </button>
            {pw && (
              <button type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(pw);
                        toast.success("Copied to clipboard");
                      }}
                      className="btn-ghost text-xs py-1 inline-flex items-center gap-1"
                      data-testid="admin-reset-copy">
                <Copy size={12} /> Copy
              </button>
            )}
          </div>
        </div>

        <div className="text-[10px] text-gray-500 leading-snug">
          Resets the password hash directly, clears brute-force lockouts and
          invalidates any pending reset tokens. The user can sign in with the
          new password immediately — no email round-trip needed.
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose}
                  className="btn-ghost text-sm py-2"
                  data-testid="admin-reset-cancel">
            Cancel
          </button>
          <button type="submit"
                  disabled={busy || !pw || pw.length < 8}
                  className="btn-primary text-sm py-2 inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid="admin-reset-submit">
            <Key size={14} /> {busy ? "Updating…" : "Set password"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminUsersPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState(null);
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  // v3.2.53 · Tabs — "active" | "deleted" | "banned"
  const [tab, setTab] = useState("active");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load users");
      setUsers([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // v3.2.53 · Split the flat user list into three groups so the tabs
  // can render their own counts and filtered views.
  const groups = useMemo(() => {
    const active = [], deleted = [], banned = [];
    for (const u of users || []) {
      if (u.deleted_action === "ban")       banned.push(u);
      else if (u.deleted_at)                deleted.push(u);
      else                                  active.push(u);
    }
    return { active, deleted, banned };
  }, [users]);

  const filtered = useMemo(() => {
    const base = groups[tab] || [];
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((u) => {
      const email  = (u.email || u.original_email || "").toLowerCase();
      const name   = (u.name  || "").toLowerCase();
      return email.includes(q) || name.includes(q);
    });
  }, [groups, tab, query]);

  // Non-admins → punt to dashboard. Done after hooks so Rules of Hooks holds.
  if (user && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  // v3.2.49 · Delete + ban handlers. Delete anonymises the account
  // (email freed for re-registration). Ban does the same plus adds
  // the original email to `banned_emails` so a fresh signup is
  // blocked with a vague 403.
  const doDelete = async (u) => {
    if (!globalThis.confirm(
      `Delete user ${u.email}?\n\n` +
      `This anonymises the account. The person will NOT be able to sign in ` +
      `with this email, but they CAN re-register from scratch. All their ` +
      `jobs, proposals, and payment history are preserved.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/delete`);
      toast.success(`Deleted ${u.email} — email freed for re-registration`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const doBan = async (u) => {
    const reason = globalThis.prompt(
      `Ban user ${u.email}?\n\n` +
      `This deletes the account AND blocks the email from ever registering ` +
      `again (until you manually unban). Optional reason:`,
      "",
    );
    if (reason === null) return;   // cancel
    try {
      await api.post(`/admin/users/${u.id}/ban`, { reason });
      toast.success(`Banned ${u.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ban failed");
    }
  };

  // v3.2.53 · Unban restores registration eligibility only; does NOT
  // resurrect the deleted account (the person must sign up fresh).
  const doUnban = async (u) => {
    const email = u.original_email || u.email;
    if (!globalThis.confirm(
      `Unban ${email}?\n\n` +
      `Removes the email from the ban list so anyone can register with it again. ` +
      `The deleted account itself is NOT restored — the person still has to sign up fresh.`,
    )) return;
    try {
      await api.post(`/admin/banned-emails/${encodeURIComponent(email)}/unban`);
      toast.success(`Unbanned ${email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unban failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#050608] text-gray-200">
      <Topbar active="admin-users" />
      <div className="p-6 md:p-10 max-w-5xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="label-tag">// Admin · user management</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold inline-flex items-center gap-3">
              <ShieldCheck size={36} className="text-[#FFCC00]" />
              Users
            </h1>
            <p className="text-sm text-gray-400 mt-2 max-w-2xl">
              Reset any user&apos;s password directly. Bypasses the email-link flow
              entirely — useful when Resend DNS isn&apos;t verified or a user is
              locked out. Brute-force counters are cleared on success.
            </p>
          </div>
          <button onClick={load}
                  disabled={busy}
                  className="btn-ghost text-sm py-2 inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid="admin-users-refresh">
            <ArrowsClockwise size={14} /> Refresh
          </button>
        </div>

        <EmailDiagnosticCard />

        {/* v3.2.53 · Active / Deleted / Banned tabs */}
        <div className="flex items-center gap-1 border-b border-white/10 mb-4"
             data-testid="admin-users-tabs">
          {[
            { key: "active",  label: "Active",  count: groups.active.length,
              tint: "text-emerald-300", border: "border-emerald-300" },
            { key: "deleted", label: "Deleted", count: groups.deleted.length,
              tint: "text-gray-300",    border: "border-gray-300" },
            { key: "banned",  label: "Banned",  count: groups.banned.length,
              tint: "text-red-300",     border: "border-red-400" },
          ].map((t) => {
            const isOn = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 -mb-px border-b-2 font-mono text-xs uppercase tracking-widest transition
                            ${isOn ? `${t.border} ${t.tint}` : "border-transparent text-gray-500 hover:text-gray-300"}`}
                data-testid={`admin-users-tab-${t.key}`}
              >
                {t.label}
                <span className={`ml-2 text-[10px] ${isOn ? "" : "text-gray-600"}`}>
                  ({t.count})
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative mb-4 max-w-sm">
          <MagnifyingGlass size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${tab} by email or name…`}
            className="input-base text-sm w-full pl-8"
            data-testid="admin-users-search"
          />
        </div>

        <div className="border border-white/10 overflow-x-auto"
             data-testid="admin-users-table">
          <table className="w-full">
            <thead className="bg-[#0a0d12] border-b border-white/10">
              <tr className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-mono text-left">
                <th className="p-3">Email</th>
                <th className="p-3">Name</th>
                <th className="p-3">Role</th>
                <th className="p-3">Created</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {busy && users === null && (
                <tr><td colSpan={5} className="p-6 text-center text-gray-500 text-sm">Loading users…</td></tr>
              )}
              {!busy && filtered.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-gray-500 text-sm">
                  {query ? `No users match "${query}"` : "No users yet."}
                </td></tr>
              )}
              {filtered.map((u) => {
                const isAdmin = (u.role || "").toLowerCase() === "admin";
                const isDeleted = !!u.deleted_at;
                return (
                  <tr key={u.id}
                      className={`border-t border-white/5 font-mono text-sm hover:bg-white/5 ${isDeleted ? "opacity-60" : ""}`}
                      data-testid={`admin-users-row-${u.email}`}>
                    <td className="p-3 text-[#00E5FF]">
                      <div className="flex items-center gap-2">
                        <span className={isDeleted ? "line-through text-gray-500" : ""}>
                          {isDeleted ? (u.original_email || u.email) : u.email}
                        </span>
                        {isDeleted && (
                          <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase
                            ${u.deleted_action === "ban"
                              ? "bg-red-500/20 text-red-300 border border-red-500/40"
                              : "bg-white/10 text-gray-400 border border-white/20"}`}
                            title={`${u.deleted_action} by ${u.deleted_by} on ${u.deleted_at}`}>
                            {u.deleted_action || "deleted"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-gray-300">{u.name || "—"}</td>
                    <td className="p-3">
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold ${
                        isAdmin ? "bg-[#FFCC00] text-black" : "bg-white/10 text-gray-300"
                      }`}>
                        {(u.role || "user").toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3 text-gray-500 text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => nav(`/admin/users/${u.id}/transactions`)}
                          className="btn-ghost text-xs py-1 inline-flex items-center gap-1"
                          data-testid={`admin-users-tx-${u.email}`}
                          title="View this user's payment history">
                          <CurrencyDollar size={12} /> Transactions
                        </button>
                        {!isDeleted && !isAdmin && (
                          <>
                            <button
                              onClick={() => setTarget(u)}
                              className="btn-ghost text-xs py-1 inline-flex items-center gap-1"
                              data-testid={`admin-users-reset-${u.email}`}>
                              <Key size={12} /> Reset password
                            </button>
                            <button
                              onClick={() => doDelete(u)}
                              className="text-xs py-1 px-2 inline-flex items-center gap-1 border border-white/15 text-gray-300 hover:border-white/40 hover:text-white transition"
                              data-testid={`admin-users-delete-${u.email}`}
                              title="Delete account (email freed for re-registration)">
                              <Trash size={12} /> Delete
                            </button>
                            <button
                              onClick={() => doBan(u)}
                              className="text-xs py-1 px-2 inline-flex items-center gap-1 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition"
                              data-testid={`admin-users-ban-${u.email}`}
                              title="Delete AND block future registration with this email">
                              <Prohibit size={12} /> Ban
                            </button>
                          </>
                        )}
                        {/* v3.2.53 · Banned row shows Unban only. Deleted (non-ban)
                             row shows nothing extra — the account is anonymised and
                             the email is already free for re-registration. */}
                        {u.deleted_action === "ban" && (
                          <button
                            onClick={() => doUnban(u)}
                            className="text-xs py-1 px-2 inline-flex items-center gap-1 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition"
                            data-testid={`admin-users-unban-${u.email}`}
                            title="Remove from ban list (allows re-registration)">
                            <ArrowsClockwise size={12} /> Unban
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-gray-500 mt-3 font-mono">
          {users ? `${filtered.length} / ${(groups[tab] || []).length} ${tab} user(s)` : ""}
        </div>
      </div>

      {target && (
        <ResetPasswordModal
          user={target}
          onClose={() => setTarget(null)}
          onDone={load}
        />
      )}
    </div>
  );
}
